import { AlertCircle, CheckCircle2 } from "lucide-react";

export const STEPS = [
  { key: "settings", label: "Payroll Settings", short: "Settings" },
  { key: "components", label: "Salary Components", short: "Components" },
  { key: "formulas", label: "Formula Builder", short: "Formulas" },
  { key: "templates", label: "Salary Templates", short: "Templates" },
  { key: "allocation", label: "Employee Allocation", short: "Allocation" },
];

export const WIZARD_STEP_KEY = "brixigo.payrollSetupWizard.step";
export const WIZARD_SELECTED_COMPONENTS_KEY = "brixigo.payrollSetupWizard.selectedComponents";

function _readSelectedStore(store) {
  try {
    const raw = store.getItem(WIZARD_SELECTED_COMPONENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.map(String) : null;
  } catch {
    return null;
  }
}

export function readWizardSelectedComponentIds() {
  // Prefer durable localStorage (survives refresh); fall back to session for in-flight wizard.
  return _readSelectedStore(localStorage) || _readSelectedStore(sessionStorage);
}

export function writeWizardSelectedComponentIds(ids = []) {
  const payload = JSON.stringify((ids || []).map(String));
  try {
    localStorage.setItem(WIZARD_SELECTED_COMPONENTS_KEY, payload);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.setItem(WIZARD_SELECTED_COMPONENTS_KEY, payload);
  } catch {
    /* ignore */
  }
}

/** Append IDs to the wizard selection (deduped). Returns the new id list. */
export function appendWizardSelectedComponentIds(idsToAdd = []) {
  const current = readWizardSelectedComponentIds() || [];
  const next = [...new Set([...current.map(String), ...(idsToAdd || []).map(String)])];
  writeWizardSelectedComponentIds(next);
  return next;
}

/** Remove IDs from the wizard selection. Returns the new id list. */
export function removeWizardSelectedComponentIds(idsToRemove = []) {
  const drop = new Set((idsToRemove || []).map(String));
  const next = (readWizardSelectedComponentIds() || []).filter((id) => !drop.has(String(id)));
  writeWizardSelectedComponentIds(next);
  return next;
}

export const INACTIVE_SELECTED_MSG =
  "Your selected component was inactive so please activate.";

export function resolveSelectedComponentIds(selectedComponentIds) {
  const fromProp = Array.isArray(selectedComponentIds) ? selectedComponentIds.map(String) : null;
  const fromStore = readWizardSelectedComponentIds();
  return (fromProp && fromProp.length ? fromProp : fromStore) || [];
}

export function inactiveSelectedComponents(comps = [], selectedIds = []) {
  const set = new Set((selectedIds || []).map(String));
  return (comps || []).filter(
    (c) => set.has(String(c.component_id)) && c.is_active === false,
  );
}

/** Starter package shown by default (matches backend seed_payroll_defaults). */
export const STANDARD_EARNING_CODES = ["BASIC", "HRA", "SPA"];
export const STANDARD_DEDUCTION_CODES = ["PF", "PT", "TDS"];
export const STANDARD_VARIABLE_CODES = ["OVERTIME", "ADVANCE_TAKEN"];
export const STANDARD_COMPONENT_CODES = new Set([
  ...STANDARD_EARNING_CODES,
  ...STANDARD_DEDUCTION_CODES,
  ...STANDARD_VARIABLE_CODES,
]);

/** Canonical formulas for the starter formula components (+ legacy SPECIAL alias). */
export const STANDARD_DEFAULT_FORMULAS = {
  BASIC: "MONTHLY_CTC * 0.50",
  HRA: "BASIC * 0.40",
  SPA: "MONTHLY_CTC - (BASIC + HRA)",
  SPECIAL: "MONTHLY_CTC - (BASIC + HRA)",
  PF: "MIN(BASIC * 0.12, 1800)",
  PT: "200",
  TDS: "0",
};

export function componentCode(c) {
  return String(c?.code || "").trim().toUpperCase();
}

export function isStandardComponent(c) {
  return STANDARD_COMPONENT_CODES.has(componentCode(c));
}

/** True when stored wizard selection only tracks variable rows (legacy 2-component view). */
export function wizardSelectionIsVariableOnly(storedIds, allComponents) {
  const ids = (storedIds || []).map(String).filter(Boolean);
  if (!ids.length) return false;
  const byId = new Map((allComponents || []).map((r) => [String(r.component_id), r]));
  return ids.every((id) => {
    const row = byId.get(id);
    if (!row) return false;
    return STANDARD_VARIABLE_CODES.includes(componentCode(row));
  });
}

/**
 * Keep wizard pipeline selection aligned with the starter payroll package.
 * Expands legacy selections that only contained OVERTIME / ADVANCE_TAKEN.
 */
export function repairWizardComponentSelection(allComponents) {
  const list = Array.isArray(allComponents) ? allComponents : [];
  const starterRows = list.filter(
    (r) =>
      r.is_active !== false &&
      (isStandardComponent(r) || r.meta?.setup_seed === true),
  );
  if (!starterRows.length) return;

  const starterIds = starterRows.map((r) => String(r.component_id));
  const stored = readWizardSelectedComponentIds() || [];

  if (stored.length === 0 || wizardSelectionIsVariableOnly(stored, list)) {
    writeWizardSelectedComponentIds(starterIds);
    return;
  }

  const storedSet = new Set(stored.map(String));
  const missing = starterIds.filter((id) => !storedSet.has(id));
  if (missing.length) {
    writeWizardSelectedComponentIds([...storedSet, ...missing]);
  }
}

export function formulaOf(c) {
  const code = componentCode(c);
  const fromApi = String(c?.formula_expression || c?.formula || "").trim();
  if (fromApi) return fromApi;
  return STANDARD_DEFAULT_FORMULAS[code] || "";
}

/** Stable order for the 10 standard components. */
export function sortStandardComponents(list) {
  const order = [...STANDARD_EARNING_CODES, ...STANDARD_DEDUCTION_CODES];
  return [...list].sort((a, b) => {
    const ai = order.indexOf(componentCode(a));
    const bi = order.indexOf(componentCode(b));
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
}

export function catOf(c) {
  return String(c.component_category || c.component_type || "").toLowerCase();
}
export function isEarn(c) {
  return catOf(c).startsWith("earn");
}
export function calcOf(c) {
  return String(c.calculation_type || c.calc_type || "fixed").toLowerCase();
}
export function apiMsg(e, fb) {
  return e?.detail || e?.response?.data?.detail || e?.message || fb;
}
export function mappingText(c) {
  const t = calcOf(c);
  const fx = formulaOf(c);
  if (t === "formula" || fx) return fx || "formula (not set)";
  if (t === "system") return `System: ${c.system_code || "not set"}`;
  return "Fixed / manual amount";
}

export const CALC_OPTS = [
  { value: "fixed", label: "Fixed / Manual" },
  { value: "formula", label: "Formula" },
  { value: "system", label: "System (PF/ESI/PT/TDS)" },
];

export const SAMPLE_CTX = {
  BASIC: 50000,
  HRA: 20000,
  CONVEYANCE: 1600,
  MEDICAL: 1250,
  GROSS: 80000,
  CTC: 1200000,
  MONTHLY_CTC: 100000,
  WORKING_DAYS: 26,
  PAYABLE_DAYS: 24,
  PAID_DAYS: 24,
  LOP_DAYS: 2,
  OT_HOURS: 10,
};

export function Banner({ tone = "info", icon: Icon = CheckCircle2, children }) {
  return (
    <div className={`ps-wiz-banner ps-wiz-banner--${tone}`} role="status">
      <Icon size={16} />
      <span>{children}</span>
    </div>
  );
}

export function StickyBar({ children }) {
  return <div className="ps-wiz-actionbar">{children}</div>;
}

export function Chip({ children }) {
  return <span className="ps-wiz-chip">{children}</span>;
}

export function WizField({ label, required, hint, error, refWrap, children }) {
  return (
    <div className="ps-wiz-field" ref={refWrap} tabIndex={-1}>
      <label className="ps-wiz-label">
        {label}
        {required ? <span className="ps-wiz-req"> *</span> : null}
      </label>
      {children}
      {error ? (
        <p className="ps-wiz-err-msg">
          <AlertCircle size={12} /> {error}
        </p>
      ) : hint ? (
        <p className="ps-wiz-hint">{hint}</p>
      ) : null}
    </div>
  );
}

export function CalcBadge({ c }) {
  const t = calcOf(c);
  const cls = t === "formula" ? "fx" : t === "system" ? "sys" : "fixed";
  return <span className={`ps-wiz-badge ps-wiz-badge--${cls}`}>{t}</span>;
}

export function empId(e) {
  return e.employee_id || e.id;
}
export function empName(e) {
  return e.full_name || [e.first_name, e.last_name].filter(Boolean).join(" ") || e.name || e.email || "Employee";
}
export function empField(e, keys) {
  for (const k of keys) {
    const v = e[k];
    if (v == null) continue;
    if (typeof v === "object") {
      if (v.name) return v.name;
      continue;
    }
    if (String(v).trim()) return String(v);
  }
  return "";
}

export const F_DEPT = ["department", "department_name"];
export const F_DESG = ["designation", "designation_name"];
export const F_BRANCH = ["work_location", "branch", "location", "branch_name"];
export const F_TYPE = ["employment_type", "employee_type", "type"];
export const F_LOC = ["location", "city", "work_location", "office_location"];
